import { PureComponent, type ReactElement, type ReactNode } from 'react';

import classNames from 'classnames';

import Checkbox from '../../../checkbox/checkbox';
import FormFieldLabel from '../../components/form_field_label/form_field_label';

import styles from './checkbox_group.module.css';

type CheckboxGroupOption = {
  label: string;
  value: string;
};

export type ValueType = string[];

export type CheckboxGroupProps<
  T extends CheckboxGroupOption = CheckboxGroupOption,
> = {
  name?: string;
  options?: T[];
  value?: ValueType;
  labelWidth?: string;
  labelClassName?: string;
  checkboxClassName?: string;
  checkboxLabelClassName?: string;
  onChange?: (value: ValueType) => void;
  componentWrapper?: (option: T, element: ReactElement) => ReactNode;
};

const defaultComponentWrapper = <T extends CheckboxGroupOption>(
  _option: T,
  element: ReactElement,
) => element;

class CheckboxGroup<
  T extends CheckboxGroupOption = CheckboxGroupOption,
> extends PureComponent<CheckboxGroupProps<T>> {
  handleChange = (checked: boolean | undefined, value: string): void => {
    const values = new Set(this.props.value ?? []);
    if (checked && !values.has(value)) {
      values.add(value);
    }
    if (!checked && values.has(value)) {
      values.delete(value);
    }

    this.props.onChange?.([...values]);
  };

  getField = (option: T): ReactNode => {
    const componentWrapper =
      this.props.componentWrapper ?? defaultComponentWrapper;
    return (
      <div className={styles.root} key={option.value}>
        <FormFieldLabel
          htmlFor={`${this.props.name}_${option.value}`}
          width={this.props.labelWidth}
          className={classNames(
            this.props.labelClassName,
            this.props.checkboxLabelClassName,
          )}
        >
          {option.label}
        </FormFieldLabel>
        {componentWrapper(
          option,
          <Checkbox
            className={this.props.checkboxClassName}
            id={`${this.props.name}_${option.value}`}
            name={option.value}
            onChange={(checked) => this.handleChange(checked, option.value)}
            value={
              this.props.value ? this.props.value.includes(option.value) : false
            }
          />,
        )}
      </div>
    );
  };

  render(): ReactNode {
    const options = this.props.options ?? [];
    return options.map((option) => this.getField(option));
  }
}

export default CheckboxGroup;
